import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import { analysisTypeLabel, mediaAnalysisImage } from '@/common/app/modules/media/mediaAnalysis'
import { articleNewPath, mediaAnalysisPath } from '@/common/app/path'
import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import PointText from '@/components/view/atoms/text/PointText'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { MediaUsageGuideModalContext } from '@/components/container/modal/mediaUsageGuide/MediaUsageGuideModalProvider'

// ステップの表現。ai=AIが処理する工程 / comingSoon=未提供の工程
type StepTone = 'default' | 'ai' | 'comingSoon'

interface Step {
  n: string
  title: string
  desc: string
  tone?: StepTone
  // 右端に置く操作ボタン（遷移先はパスビルダで解決する）
  action?: { label: string; path: string }
  // このステップ内にサイト現状分析5種のカードを並べるか
  analysisCards?: boolean
}

interface Section {
  title: string
  desc?: string
  // 各ステップの背景をAI配色にする（推奨アクション起点＝AI主導のフロー）
  aiTinted?: boolean
  steps: Step[]
}

// アップロード（公開）は未提供のため全フロー共通で Coming Soon 表示とする
const UPLOAD_STEP = (n: string): Step => ({
  n,
  title: 'アップロード（公開） Coming Soon',
  desc: '承認済みの記事を、Smart HubからW2 Commerceに自動でアップロード/更新します。',
  tone: 'comingSoon'
})

const EDIT_APPROVAL_DESC =
  '生成された記事内容を確認し編集します。保存後に承認依頼をすることで、記事内容を承認者にて確認するフローを通します。'

// 推奨アクション起点のフローで選べるサイト現状分析（画像・ラベルは分析種別の定義を流用）
const ANALYSIS_CARDS: { type: MediaAnalysisType; desc: string }[] = [
  { type: MediaAnalysisType.COMPETITOR, desc: '課題を横断的に把握' },
  { type: MediaAnalysisType.COMPETITOR_SERP, desc: '検索結果と競合を分析' },
  { type: MediaAnalysisType.CONTENT_GAP, desc: '不足テーマを発見' },
  { type: MediaAnalysisType.CANNIBALIZATION, desc: '記事の重複を確認' },
  { type: MediaAnalysisType.LINK_STRUCTURE, desc: '記事同士のつながりを改善' }
]

const sections = (siteId: number): Section[] => [
  {
    title: '推奨アクションから記事を作成する',
    aiTinted: true,
    steps: [
      {
        n: '01',
        title: '5つのサイト調査から選んで実行',
        desc: '',
        tone: 'ai',
        analysisCards: true
      },
      {
        n: '02',
        title: '推奨アクションを確認',
        desc: '分析結果から、優先度の高い対応内容と理由を確認します。'
      },
      {
        n: '03',
        title: '自動記事作成',
        desc: '推奨キーワードやテーマを引き継ぎ、記事のたたき台を自動生成します。',
        tone: 'ai'
      },
      { n: '04', title: '内容を編集・承認依頼', desc: EDIT_APPROVAL_DESC },
      UPLOAD_STEP('05')
    ]
  },
  {
    title: '手動で新しい記事を作成する',
    desc: '狙うキーワードを決めた後、確認の細かさに合わせて2つの作成方法から選びます。',
    steps: [
      {
        n: '01',
        title: 'キーワード選定',
        desc: '検索数や競合性を確認し、記事で狙うキーワードを決めます。',
        tone: 'ai',
        action: { label: '新規記事作成', path: articleNewPath(siteId) }
      },
      {
        n: '02',
        title: '構成案の生成',
        desc: '選定したキーワードからAIが構成案を生成します。',
        tone: 'ai'
      },
      {
        n: '03',
        title: '記事本文の生成',
        desc: '構成案からAIが記事本文を生成します。選定したキーワードから直接本文を生成することも可能です。',
        tone: 'ai'
      },
      { n: '04', title: '内容を編集・承認依頼', desc: EDIT_APPROVAL_DESC },
      UPLOAD_STEP('05')
    ]
  },
  {
    title: '作成済みの記事を改善する',
    desc: '公開済みの記事をリライトし、確認・承認後に変更内容を反映します。',
    steps: [
      {
        n: '01',
        title: 'カニバリ分析',
        desc: '検索数や競合性を確認し、記事で狙うキーワードを決めます。',
        tone: 'ai',
        action: { label: '今すぐ実行', path: mediaAnalysisPath(siteId) }
      },
      {
        n: '02',
        title: 'リライトを実行',
        desc: '推奨アクションから改善したい記事を選び、リライト案AI生成を実行します。',
        tone: 'ai'
      },
      { n: '03', title: '内容を反映・承認依頼', desc: EDIT_APPROVAL_DESC },
      UPLOAD_STEP('04')
    ]
  }
]

// 分析の推奨頻度
const FREQUENCIES = [
  {
    type: MediaAnalysisType.COMPETITOR,
    frequency: '四半期に1回',
    note: '主要競合の追加・変更時は随時実行'
  },
  {
    type: MediaAnalysisType.COMPETITOR_SERP,
    frequency: '月1回',
    note: '記事作成前や順位の大幅変動時にも実行'
  },
  {
    type: MediaAnalysisType.CONTENT_GAP,
    frequency: '四半期に1回',
    note: '記事計画の見直し時にも実行'
  },
  {
    type: MediaAnalysisType.CANNIBALIZATION,
    frequency: '月1回',
    note: '新規記事をまとめて公開した後にも実行'
  },
  { type: MediaAnalysisType.LINK_STRUCTURE, frequency: '月1回', note: '記事の追加・削除後にも実行' }
]

// 推奨運用ルーティン
const ROUTINES = [
  {
    period: '毎月',
    types: [
      MediaAnalysisType.COMPETITOR_SERP,
      MediaAnalysisType.CANNIBALIZATION,
      MediaAnalysisType.LINK_STRUCTURE
    ]
  },
  { period: '四半期', types: [MediaAnalysisType.COMPETITOR, MediaAnalysisType.CONTENT_GAP] }
]

// 節見出し（見出し＋補足文）
const SectionHeading = ({ title, desc }: { title: string; desc?: string }) => (
  <div className='flex flex-col gap-1'>
    <Text size={16} weight='bold'>
      {title}
    </Text>
    {desc && (
      <Text size={12} color={TextColor.SECONDARY}>
        {desc}
      </Text>
    )}
  </div>
)

// サイト現状分析5種のカード
const AnalysisCards = () => (
  <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5'>
    {ANALYSIS_CARDS.map((c) => (
      <InsideArea key={c.type} border>
        <ItemAlign direction='vertical' align='center' gapY={4}>
          <img
            src={mediaAnalysisImage(c.type)}
            width={72}
            height={48}
            alt={analysisTypeLabel(c.type)}
          />
          <Text size={14} weight='bold' align='center'>
            {analysisTypeLabel(c.type)}
          </Text>
          <Text size={12} align='center'>
            {c.desc}
          </Text>
        </ItemAlign>
      </InsideArea>
    ))}
  </div>
)

interface StepRowProps {
  step: Step
  // 節がAI主導のフローの場合、未提供ステップ以外の背景をAI配色にする
  aiTinted?: boolean
  onAction(path: string): void
}

// ステップ1件（番号バッジ＋タイトル＋説明＋任意の操作ボタン）
const StepRow = ({ step, aiTinted, onAction }: StepRowProps) => {
  const tone = step.tone ?? 'default'
  const badgeType =
    tone === 'ai'
      ? BadgeType.SQUARE_AI
      : tone === 'comingSoon'
        ? BadgeType.SQUARE_DISABLED
        : BadgeType.SQUARE_SECONDARY

  const body = (
    <div className='flex flex-col gap-2 p-1'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <ItemAlign gapX={8} align='center' nowrap>
          <Badge size={12} badgeType={badgeType}>
            {step.n}
          </Badge>
          {tone === 'comingSoon' ? (
            <Text size={14} color={TextColor.DISABLED}>
              {step.title}
            </Text>
          ) : (
            <Text size={14} weight='bold' color={tone === 'ai' ? TextColor.AI : TextColor.PRIMARY}>
              {step.title}
            </Text>
          )}
        </ItemAlign>
        {step.action && (
          <Button color={ButtonColor.SECONDARY} onClick={() => onAction(step.action!.path)}>
            {step.action.label}
          </Button>
        )}
      </div>
      {step.desc && (
        <Text
          size={12}
          color={tone === 'comingSoon' ? TextColor.DISABLED : TextColor.SECONDARY}
          lineHeight={150}
        >
          {step.desc}
        </Text>
      )}
      {step.analysisCards && <AnalysisCards />}
    </div>
  )

  // 未提供ステップはグレー、AI主導フローはAI配色、それ以外は白＋枠線
  if (tone === 'comingSoon') return <InsideArea color={BgColor.SECONDARY}>{body}</InsideArea>
  if (aiTinted) return <AiInsideArea>{body}</AiInsideArea>
  return <InsideArea border>{body}</InsideArea>
}

// Media-DRIVER 使い方アドバイスモーダル（記事作成の3フロー＋分析の推奨頻度）
const MediaUsageGuideModal = () => {
  const { modal, showing, hide } = useContext(MediaUsageGuideModalContext)
  const navigate = useNavigate()

  const goAndClose = (path: string) => {
    navigate(path)
    hide()
  }

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text='Media-DRIVER 使い方アドバイス' onClickClose={hide} />
      <ModalContent>
        <div className='flex flex-col gap-6'>
          {sections(modal.siteId).map((section) => (
            <div key={section.title} className='flex flex-col gap-2'>
              <SectionHeading title={section.title} desc={section.desc} />
              <div className='flex flex-col gap-2'>
                {section.steps.map((step) => (
                  <StepRow
                    key={step.n}
                    step={step}
                    aiTinted={section.aiTinted}
                    onAction={goAndClose}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* 分析の推奨頻度 */}
          <div className='flex flex-col gap-2'>
            <SectionHeading title='分析の推奨頻度' />
            <div className='overflow-x-auto'>
              <table className='w-full border-collapse'>
                <thead>
                  <TableRow head>
                    {['データ', '頻度', '説明'].map((h) => (
                      <TableHeadItem key={h} type='thin'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          {h}
                        </Text>
                      </TableHeadItem>
                    ))}
                  </TableRow>
                </thead>
                <tbody>
                  {FREQUENCIES.map((f) => (
                    <TableRow key={f.type}>
                      <TableRowItem>
                        <Text size={14} weight='bold'>
                          {analysisTypeLabel(f.type)}
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={14} weight='bold' color={TextColor.GREEN}>
                          {f.frequency}
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12}>{f.note}</Text>
                      </TableRowItem>
                    </TableRow>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 推奨運用ルーティン */}
          <div className='flex flex-col gap-2'>
            <SectionHeading title='推奨運用ルーティン' />
            <div className='grid grid-cols-1 gap-2 lg:grid-cols-2'>
              {ROUTINES.map((r) => (
                <InsideArea key={r.period} color={BgColor.SECONDARY}>
                  <div className='flex flex-col gap-1.5'>
                    <Text size={14} weight='bold'>
                      {r.period}
                    </Text>
                    <PointText texts={r.types.map((type) => analysisTypeLabel(type))} />
                  </div>
                </InsideArea>
              ))}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default MediaUsageGuideModal
